import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	CancellationContext,
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import AuxDataView from '../../auxData/AuxDataView';
import { StyledContentPane } from '../../common';
import DisabledOverlay from '../../DisabledOverlay';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import CancellationLayout from '../../layouts/CancellationLayout';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import ProtectedRoute from '../../ProtectedRoute';
import CancellationDetails from '../Cancellations/CancellationDetails';
import CancellationPricePreview from '../Cancellations/CancellationPricePreview';
import DocumentsView from '../Cancellations/DocumentsView';
import InvoicesView from '../Cancellations/InvoicesView';
import InvoiceView from '../Cancellations/InvoiceView';
import Reinstatements from '../Cancellations/Reinstatements';
import CancellationSidebar from '../Sidebar/CancellationSidebar';

interface Props {
	cancellationData: CancellationData;
	authData: AuthData;
}

// Route: '.../policy/:policyId/cancellations/:cancellationId'
const CancellationRoutes = (props: Props) => {
	const { cancellationData } = props;
	const {
		selectedCancellation,
		setSelectedCancellation,
		setCancellationUpdateObj,
	} = cancellationData;
	const navigate = useNavigate();
	const params = useParams();
	const { cancellationId, policyId } = params;
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		const source = axios.CancelToken.source();

		const loadData = async () => {
			try {
				setLoading(true);
				const success = await cancellationData.loadData(
					policyId,
					cancellationId,
					{ cancelToken: source.token },
				);

				if (success) {
					setLoading(false);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				}
			}
		};

		if (policyId && cancellationId) {
			loadData();
		}

		return () => {
			source.cancel();
		};
	}, [cancellationId]);

	useEffect(() => {
		return () => {
			setSelectedCancellation(null);
			setCancellationUpdateObj({});
		};
	}, []);

	const renderMain = () => {
		if (loading || !selectedCancellation || !cancellationData) {
			return <LargeCenterSpinner />;
		}

		return (
			<StyledContentPane>
				<CancellationLayout>
					<Routes>
						<Route path='details' element={<CancellationDetails />} />
						<Route
							path='documents'
							element={
								<DocumentsView documents={selectedCancellation.documents} />
							}
						/>
						<Route
							path='invoices/:invoiceId'
							element={
								<ProtectedRoute permission='invoice.fetch'>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission='invoice.fetch'>
									<InvoicesView
										invoiceLocator={selectedCancellation.invoiceLocator}
									/>
								</ProtectedRoute>
							}
						/>
						<Route path='pricing' element={<CancellationPricePreview />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={cancellationId}
										entityType='cancellation'
									/>
								</ProtectedRoute>
							}
						/>
						<Route
							path='reinstatements'
							element={
								<ProtectedRoute permission='reinstatement.fetch'>
									<Reinstatements />
								</ProtectedRoute>
							}
						/>
						<Route path='/' element={<Navigate to='details' replace />} />
					</Routes>
				</CancellationLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (loading || !selectedCancellation || !cancellationData) {
			return <DisabledOverlay />;
		}

		return <CancellationSidebar />;
	};

	return (
		<CancellationContext.Provider value={cancellationData}>
			<LeftSidebarLayout
				leftSidebarContent={renderSidebar()}
				mainContent={renderMain()}
			/>
		</CancellationContext.Provider>
	);
};

export default withCancellationContext<Props>(CancellationRoutes);
